import { ObservableValue } from "@signal-app/observable";
import range from "lodash/range.js";
import throttle from "lodash/throttle.js";
import { MIDIControlEvents } from "midifile-ts";
import { EventScheduler } from "./EventScheduler.js";
import { controllerMidiEvent, gsResetMidiEvent } from "./MidiEventFactory.js";
const TIMER_INTERVAL = 50;
const LOOK_AHEAD_TIME = 50;
export const DEFAULT_TEMPO = 120;
export class Player {
    output;
    eventSource;
    scheduler = null;
    interval = null;
    _currentTempo = new ObservableValue(DEFAULT_TEMPO);
    _currentTick = new ObservableValue(0);
    _isPlaying = new ObservableValue(false);
    _loop = new ObservableValue(null);
    disableSeek = false;
    onPositionChanged;
    onIsPlayingChanged;
    onLoopChanged;
    constructor(output, eventSource) {
        this.output = output;
        this.eventSource = eventSource;
        this.onPositionChanged = this._currentTick.onChanged;
        this.onIsPlayingChanged = this._isPlaying.onChanged;
        this.onLoopChanged = this._loop.onChanged;
    }
    play = () => {
        if (this.isPlaying) {
            console.warn("called play() while playing. aborted.");
            return;
        }
        this.scheduler = new EventScheduler(this.eventSource, () => this.allNotesOffEvents(), () => this.allSoundsOffEvents(), this._currentTick.value, TIMER_INTERVAL + LOOK_AHEAD_TIME);
        this._isPlaying.set(true);
        this.output.activate();
        this.interval = window.setInterval(() => this._onTimer(), TIMER_INTERVAL);
        this.output.activate();
    };
    set position(tick) {
        if (!Number.isInteger(tick)) {
            console.warn("Player.tick should be an integer", tick);
        }
        if (this.disableSeek) {
            return;
        }
        tick = Math.min(Math.max(Math.floor(tick), 0), this.eventSource.endOfSong);
        this.scheduler?.scheduleSeek(tick);
        this._currentTick.set(tick);
    }
    get position() {
        return this._currentTick.value;
    }
    get isPlaying() {
        return this._isPlaying.value;
    }
    get loop() {
        return this._loop.value;
    }
    set loop(value) {
        this._loop.set(value);
    }
    get numberOfChannels() {
        return 16;
    }
    allSoundsOffChannel = (ch) => {
        this.scheduler?.enqueueEvents([
            {
                ...controllerMidiEvent(0, ch, MIDIControlEvents.ALL_SOUNDS_OFF, 0),
                trackId: null,
            },
        ]);
    };
    allSoundsOff = () => {
        for (const ch of range(0, this.numberOfChannels)) {
            this.allSoundsOffChannel(ch);
        }
    };
    allSoundsOffExclude = (channel) => {
        for (const ch of range(0, this.numberOfChannels)) {
            if (ch !== channel) {
                this.allSoundsOffChannel(ch);
            }
        }
    };
    allNotesOffEvents() {
        return range(0, this.numberOfChannels).map((ch) => ({
            ...controllerMidiEvent(0, ch, MIDIControlEvents.ALL_NOTES_OFF, 0),
            trackId: null, // do not mute
        }));
    }
    allSoundsOffEvents() {
        return range(0, this.numberOfChannels).map((ch) => ({
            ...controllerMidiEvent(0, ch, MIDIControlEvents.ALL_SOUNDS_OFF, 0),
            trackId: null, // do not mute
        }));
    }
    resetControllers() {
        // RP-15 controller reset (it does not do a full reset)
        for (const ch of range(0, this.numberOfChannels)) {
            this.sendEvent(controllerMidiEvent(0, ch, MIDIControlEvents.RESET_CONTROLLERS, 0x7f));
        }
        // Full GS reset
        this.sendEvent(gsResetMidiEvent(0, [
            0x41, // Roland
            0x10, // Device ID (defaults to 16 on Roland)
            0x42, // GS
            0x12, // Command ID (DT1)
            0x40, // System parameter - Address
            0x00, // Global parameter -  Address
            0x7f, // GS Change - Address
            0x00, // Turn on - Data
            0x41, // Checksum
            0xf7, // End of exclusive
        ]));
    }
    stop = () => {
        this.scheduler?.scheduleStop();
        // prevent a pending throttled sync from overwriting a position set right after stop()
        this.syncPosition.cancel();
    };
    finalizeStop() {
        this.scheduler = null;
        this._isPlaying.set(false);
        if (this.interval !== null) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }
    reset = () => {
        this.resetControllers();
        this.stop();
        this._currentTick.set(0);
    };
    get currentTempo() {
        return this._currentTempo.value;
    }
    set currentTempo(value) {
        this._currentTempo.set(value);
    }
    // delayTime: seconds, timestampNow: milliseconds
    sendEvent = (event, delayTime = 0, timestampNow = performance.now(), trackId) => {
        this.output.sendEvent(event, delayTime, timestampNow, trackId);
    };
    syncPosition = throttle(() => {
        if (this.scheduler !== null) {
            this._currentTick.set(this.scheduler.scheduledTick);
        }
    }, 50);
    applyPlayerEvent(e) {
        if (e.type !== "channel" && "subtype" in e) {
            switch (e.subtype) {
                case "setTempo":
                    this._currentTempo.set(60000000 / e.microsecondsPerBeat);
                    break;
                default:
                    break;
            }
        }
    }
    _onTimer() {
        if (this.scheduler === null) {
            return;
        }
        const timestamp = performance.now();
        this.scheduler.loop = this.loop?.enabled ? this.loop : null;
        const { events, shouldStop } = this.scheduler.readNextEvents(this._currentTempo.value, timestamp);
        events.forEach(({ event: e, timestamp: time }) => {
            if (e.type === "channel" ||
                e.type === "sysEx" ||
                e.type === "dividedSysEx") {
                const delayTime = (time - timestamp) / 1000;
                this.sendEvent(e, delayTime, timestamp, e.trackId ?? undefined);
            }
            else {
                this.applyPlayerEvent(e);
            }
        });
        if (shouldStop) {
            this.finalizeStop();
        }
        this.syncPosition();
    }
    // convenience methods
    playOrPause = () => {
        if (this.isPlaying) {
            this.stop();
        }
        else {
            this.play();
        }
    };
    setLoopBegin = (tick) => {
        this.loop = {
            end: Math.max(tick, this.loop?.end ?? tick),
            enabled: this.loop?.enabled ?? false,
            begin: tick,
        };
    };
    setLoopEnd = (tick) => {
        this.loop = {
            begin: Math.min(tick, this.loop?.begin ?? tick),
            enabled: this.loop?.enabled ?? false,
            end: tick,
        };
    };
    toggleEnableLoop = () => {
        if (this.loop === null) {
            return;
        }
        this.loop = { ...this.loop, enabled: !this.loop.enabled };
    };
}
//# sourceMappingURL=Player.js.map